import { useTheme } from "@/hooks/useTheme";
import { getContrastTextColor } from "@/lib/utils";
import cognitoService from "@/services/cognitoService";
import { authService } from "@/services/userService";
import { useUserStore } from "@/stores/userStore";
import { useQueryClient } from "@tanstack/react-query";
import type { MenuProps } from "antd";
import { Avatar, Button, Dropdown, message } from "antd";
import { LogOut, Menu, User } from "lucide-react";
import { useNavigate } from "react-router";
import NotificationDropdown from "@/components/NotificationDropdown";

interface HeaderProps {
  showSidePanel: boolean;
  toggleSidePanel: (state: boolean) => void;
}

const Header = ({ showSidePanel, toggleSidePanel }: HeaderProps) => {
  const navigate = useNavigate();
  const { user, logout } = useUserStore();
  const { theme } = useTheme();
  const queryClient = useQueryClient();

  /**
   * Handles user logout
   */
  const handleLogout = async () => {
    try {
      if (user?.authProvider === "cognito") {
        // Clear local state first
        logout();
        queryClient.clear();

        // Redirect to Cognito logout (this will redirect the browser to Cognito, then back to /login)
        await cognitoService.logout();
      } else {
        // Call backend logout
        await authService.logout();

        // Clear local state for non-Cognito users
        queryClient.clear();
        logout();
        navigate("/login");
        message.success("Logged out successfully");
      }
    } catch (error) {
      console.error("Logout error:", error);
      // Even if API fails, clear local session and cache
      queryClient.clear();
      logout();
      navigate("/login");
    }
  };

  /**
   * Handles navigation to profile edit page
   */
  const handleEditProfile = () => {
    navigate("/profile");
  };

  /**
   * Avatar dropdown menu items
   */
  const menuItems: MenuProps["items"] = [
    {
      key: "profile",
      icon: <User className="h-4 w-4" />,
      label: "Edit Profile",
      onClick: handleEditProfile,
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <LogOut className="h-4 w-4" />,
      label: "Logout",
      onClick: handleLogout,
      danger: true,
    },
  ];

  /**
   * Get user initials for avatar
   */
  const getUserInitials = () => {
    if (!user) return "U";
    const firstInitial = user.firstName?.charAt(0) || "";
    const lastInitial = user.lastName?.charAt(0) || "";
    return (firstInitial + lastInitial).toUpperCase() || "U";
  };

  /**
   * Get user full name
   */
  const getUserFullName = () => {
    if (!user) return "User";
    return `${user.firstName || ""} ${user.lastName || ""}`.trim() || "User";
  };

  return (
    <header className="flex h-16 items-center justify-between shadow-sm bg-white px-4 lg:px-6 shrink-0">
      <div className="flex items-center gap-4">
        <Button type="text" icon={<Menu className="h-5 w-5" />} onClick={() => toggleSidePanel(!showSidePanel)} />
      </div>

      <div className="flex items-center gap-4">
        <NotificationDropdown />

        <Dropdown menu={{ items: menuItems }} placement="bottomRight" trigger={["click"]}>
          <div className="flex items-center gap-2 cursor-pointer hover:opacity-80">
            <Avatar
              src={user?.profilePicture}
              style={{
                backgroundColor: theme.primaryColor,
                color: getContrastTextColor(theme.primaryColor),
                border: "solid 0.5px gray",
              }}
              size="default"
            >
              {!user?.profilePicture && getUserInitials()}
            </Avatar>
            <div>
              <div className="hidden md:block text-sm font-medium">{getUserFullName()}</div>
              <div className="hidden md:block text-xs font-light">{user?.email}</div>
            </div>
          </div>
        </Dropdown>
      </div>
    </header>
  );
};

export default Header;
